import { workflowRequest as request } from "./workflowTransport"
import type {
  WorkflowPage,
  CorrectiveAction,
  CorrectiveActionWrite,
  ActionDetail,
  InspectionLifecycle,
  InspectionState,
  InspectionPlan,
  InspectionPlanWrite,
  PlannedInspection,
  InspectionComparison,
  SiteWorkflowOverview,
  WorkflowEvent,
  WorkflowUser,
  AnswerFollowUp,
  InspectionSnapshot,
} from "../types/siteWorkflows"
const enc = encodeURIComponent
const query = (values: Record<string, string | number | boolean | undefined>) => {
  const p = new URLSearchParams()
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== "") p.set(k, String(v))
  })
  return `?${p}`
}
export const siteWorkflowApi = {
  overview: (
    enterpriseUuid: string,
    q: string,
    archived: boolean,
    page: number,
    signal?: AbortSignal,
  ) =>
    request<WorkflowPage<SiteWorkflowOverview>>(
      `site-workflows${query({ enterpriseUuid, q, archived, limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  users: (enterprise: string, signal?: AbortSignal) =>
    request<WorkflowUser[]>(
      `enterprise/users?enterprise_uuid=${enc(enterprise)}`,
      signal,
      "GET",
      undefined,
      true,
    ),
  plans: (site: string, page: number, signal?: AbortSignal) =>
    request<WorkflowPage<InspectionPlan>>(
      `physical-sites/${enc(site)}/inspection-plans${query({ limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  savePlan: (site: string, body: InspectionPlanWrite, existing?: string) =>
    request<InspectionPlan>(
      existing
        ? `inspection-plans/${enc(existing)}`
        : `physical-sites/${enc(site)}/inspection-plans`,
      undefined,
      existing ? "PUT" : "POST",
      body,
    ),
  occurrences: (site: string, status: string, page: number, signal?: AbortSignal) =>
    request<WorkflowPage<PlannedInspection>>(
      `physical-sites/${enc(site)}/planned-inspections${query({ status, limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  occurrence: (value: PlannedInspection, operation: string, reason: string, dueDate?: string) =>
    request<PlannedInspection>(
      `planned-inspections/${enc(value.uuid)}/${enc(operation)}`,
      undefined,
      "POST",
      { expectedVersion: value.version, expectedPlanVersion: value.planVersion, reason, dueDate },
    ),
  actions: (
    site: string,
    status: string,
    page: number,
    signal?: AbortSignal,
    assetUuid?: string,
    componentUuid?: string,
  ) =>
    request<WorkflowPage<CorrectiveAction>>(
      `physical-sites/${enc(site)}/corrective-actions${query({ status, assetUuid, componentUuid, limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  action: (id: string, signal?: AbortSignal) =>
    request<ActionDetail>(`corrective-actions/${enc(id)}`, signal),
  saveAction: (site: string, body: CorrectiveActionWrite, existing?: string) =>
    request<CorrectiveAction>(
      existing
        ? `corrective-actions/${enc(existing)}`
        : `physical-sites/${enc(site)}/corrective-actions`,
      undefined,
      existing ? "PUT" : "POST",
      body,
    ),
  events: (site: string, kind: string, id: string, page: number, signal?: AbortSignal) =>
    request<WorkflowPage<WorkflowEvent>>(
      `physical-sites/${enc(site)}/workflow-events${query({ subjectKind: kind, subjectUuid: id, limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  comment: (action: CorrectiveAction, reason: string) =>
    request<{ ok: boolean }>(`corrective-actions/${enc(action.uuid)}/events`, undefined, "POST", {
      expectedVersion: action.version,
      reason,
    }),
  observe: (action: CorrectiveAction, sourceAnswerUuid: string, reason: string) =>
    request<{ ok: boolean }>(
      `corrective-actions/${enc(action.uuid)}/observations`,
      undefined,
      "POST",
      { expectedVersion: action.version, sourceAnswerUuid, reason },
    ),
  lifecycle: (id: string, signal?: AbortSignal) =>
    request<InspectionLifecycle>(`inspections/${enc(id)}/lifecycle`, signal),
  saveLifecycle: (value: InspectionLifecycle, state: InspectionState, reason: string) =>
    request<InspectionLifecycle>(
      `inspections/${enc(value.inspectionUuid)}/lifecycle`,
      undefined,
      "PUT",
      { expectedVersion: value.version, state, reason },
    ),
  findings: (id: string, signal?: AbortSignal) =>
    request<InspectionSnapshot>(`inspections/${enc(id)}/findings`, signal),
  followUp: (answer: string, signal?: AbortSignal) =>
    request<AnswerFollowUp>(`answer-follow-up/${enc(answer)}`, signal),
  inspections: (site: string, page: number, signal?: AbortSignal) =>
    request<
      WorkflowPage<{
        uuid: string
        missionName: string
        siteName: string
        executionDate: string | null
      }>
    >(
      `physical-sites/${enc(site)}/inspections${query({ limit: 50, offset: (page - 1) * 50 })}`,
      signal,
    ),
  compare: (
    site: string,
    before: string,
    after: string,
    kind: string,
    page: number,
    signal?: AbortSignal,
  ) =>
    request<InspectionComparison>(
      `physical-sites/${enc(site)}/inspection-comparison${query({ fromInspectionUuid: before, toInspectionUuid: after, kind, limit: 20, offset: (page - 1) * 20 })}`,
      signal,
    ),
  calendar: async (site: string, fromDate: string, toDate: string, signal?: AbortSignal) => {
    const items: PlannedInspection[] = []
    let total = 0
    do {
      const value = await request<WorkflowPage<PlannedInspection>>(
        `physical-sites/${enc(site)}/planned-inspections${query({ fromDate, toDate, limit: 200, offset: items.length })}`,
        signal,
      )
      items.push(...value.items)
      total = value.total
      if (!value.items.length) break
    } while (items.length < total)
    return items
  },
  evidence: (action: string, file: File) => {
    const form = new FormData()
    form.append("file", file)
    return request<{ uuid: string }>(
      `corrective-actions/${enc(action)}/evidence`,
      undefined,
      "POST",
      form,
    )
  },
}
export function workflowError(e: unknown) {
  const error = e as { status?: number; message?: string }
  if (error?.status === 409)
    return "Les données ont changé ou cet enregistrement existe déjà. Votre saisie est conservée. Rechargez avant de réessayer."
  if (error?.status === 404)
    return "Élément indisponible. Vérifiez les droits et le rattachement de l’inspection et de ses équipements au site."
  if (error?.status === 403)
    return "Cette opération nécessite un administrateur habilité à vérifier les actions."
  return error?.message || "Le serveur est indisponible. Votre saisie est conservée."
}
